// WHAT IS IN EACH ROLE'S SIDEBAR, AND IN WHAT ORDER. Presentation, and since
// the route tree landed, ONLY presentation.
//
// It answers two questions now:
//
//   1. What is in this role's sidebar          (app-sidebar.tsx)
//   2. Where "/" drops this role               (homeRouteFor — the first entry)
//
// ⚠️ IT USED TO ANSWER TWO MORE — may this role open this page, and may they
// run what is on it — and that conflation is what this file was split to end.
// A nav entry was being asked to be a sidebar link AND a permission scope, which
// is why isNavActive below still carries hand-written carve-outs for
// sibling entries over one subtree. Those are a highlighting concern and nothing
// else; they have no security weight any more.
//
// The permission is lib/route-access.ts — a sparse, nested table of URLs, where
// a path appears only if its answer differs from its parent's. Read that file to
// change who sees what; changing a list here changes a sidebar.
//
// ⚠️ SO ADDING A PAGE IS NO LONGER ADDING A LINE HERE. A page inherits the
// permission of the segment it sits under, and appears in a sidebar only if
// somebody wants a link to it. The two facts became independent on purpose: the
// old rule made "unreachable" and "unlinked" the same state, and the price was
// that /scripts/videos and /scripts/editor were never gated at all — every role
// had them, so nothing could observe the gate was missing until a role arrived
// that did not.

import {
  MonitorPlay, HelpCircle, MessageSquare, Sparkles, GraduationCap, Settings, CalendarDays, Users, Gauge, Rocket, ListChecks, UserSearch, BarChart3, Building2, BookOpen, Clock, FileText, Megaphone, ChartLine, KeyRound, Coins, Activity, Timer, Inbox, AtSign, ListTodo, CalendarClock, ToggleRight, ClipboardCheck, type LucideIcon,
} from "lucide-react";
import type { Persona } from "@/lib/persona-types";
import { SKILLS_PATH } from "@/lib/skill-pages";

/**
 * THE HEADINGS THE SIDEBAR GROUPS UNDER, in the order they appear.
 *
 * The strings ARE the headings — there is no second table mapping a key to a
 * label, because a heading has no other job and the indirection would only be
 * one more thing to keep in step.
 *
 * ⚠️ A ROLE WITH ONE CATEGORY GETS NO HEADINGS AT ALL. A creator's three pages
 * are all Creator Scripts, and a heading over the whole of a list says nothing
 * the list does not already say — it just indents everything. The rule lives
 * in navSectionsFor's caller, not here.
 */
// ⚠️ "Reports" SITS BETWEEN THE WORK AND THE CONSOLE, and the position is the
// argument for the heading existing at all. Client Hours is not Onboarding — it
// counts the onboarding gates, but it also counts scripts and pitches, and
// filing it under any one of the three would name it after a third of itself.
// Nor is it Admin, which is what running the APP takes; this is a reading OF the
// work. A one-entry section is not a mistake here, for the reason Brand Health
// gives one heading up: the rule that a lone category gets no heading is about a
// role whose WHOLE nav is one category.
//
// ⚠️ NO "Skills" HEADING: a skill each would clutter the sidebar. Every skill
// is a row on the Skills page, one entry under Admin; the heading and its
// twenty items are gone.
export const NAV_CATEGORIES = ["Creator Scripts", "Brands", "Onboarding", "Reports", "Admin"] as const;

export type NavCategory = (typeof NAV_CATEGORIES)[number];

export interface NavItem {
  href: string;
  icon: LucideIcon;
  label: string;
  /**
   * ⚠️ REQUIRED, AND THAT IS THE POINT. Grouping could have been a second map
   * from href to heading, and then a new entry could reach a role's list while
   * reaching no heading — the same "forgot the other place" failure the note at
   * the top of this file exists to make impossible. Here the type checker asks
   * the question at the moment you declare the page.
   */
  category: NavCategory;
}

// The week's scripts. Labelled "Scripts" — the table is a list of scripts to
// write and read, and "Videos" named the eventual output rather than the work.
// The href keeps its old path; renaming a label is not a reason to break URLs.
export const SCRIPTS: NavItem = { href: "/scripts/videos", icon: MonitorPlay, label: "Scripts", category: "Creator Scripts" };
export const TRAINING_GUIDE: NavItem = { href: "/scripts/guide", icon: GraduationCap, label: "Training Guide", category: "Creator Scripts" };

// Admin keeps Settings: it is the only way to connect Drive, without which a
// pasted footage link cannot be validated before an AM finds out the hard way.
// It also carries the four sourcing feed syncs, which spend on that same grant.
//
// ⚠️ THE HREF MOVED DOWN A LEVEL, and the reason is nesting rather than taste.
// This page was AT /settings and was admin-only, while /settings/users and
// /settings/onboarding-template sat beneath it holding admin AND AM Ops — a
// child wider than its parent, which is exactly why those two had to carry
// their own canOpen call instead of a layout gate (page-gate.tsx documented it
// as the one exception). With the admin-only page moved to a node of its own,
// /settings became the section — the union of what is under it — the nesting
// holds, and the exception is deleted. See lib/route-access.ts.
//
// The LABEL is unchanged: to an admin this is still "Settings", and renaming a
// nav entry is a separate decision from moving a route.
export const SETTINGS: NavItem = { href: "/settings/integrations", icon: Settings, label: "Settings", category: "Admin" };

// Provisioning. The page that replaced IRONSIDE_IDENTITY_MAP: onboarding someone
// is a row in a table now, not an env var edit and a container restart.
//
// It lives UNDER /settings, which makes it the one nav href that is a prefix of
// another — the exact case the comment on isNavActive warns about. Handled there,
// explicitly, rather than by moving the route somewhere it does not belong.
export const USERS: NavItem = { href: "/settings/users", icon: Users, label: "Users", category: "Admin" };

// Brands, the vendor identities that measure them, and the grants that read
// those. It sits beside Users because it is the other half of the same job —
// /settings/users staffs PEOPLE onto brands, this page is where a brand exists
// at all — and because a badge belongs next to the queue it counts.
//
// ⚠️ ITS ROUTE_ACCESS LINE IS ["admin","am_ops"], its own: /settings is the AM's
// too (for Your Slack), and this page is not. This entry grants nothing; it
// links to a page the line admits.
export const BRANDS_ADMIN: NavItem = { href: "/settings/brands", icon: Building2, label: "Brands & access", category: "Admin" };

// A brand's cadence — when each step of a batch is due. THE AM'S, OPS' AND THE
// ADMIN'S: a creator reads the dates it produces and never sets them, and the
// three roles that run the agency set them.
//
// ⚠️ THE ADMIN GOT IT BACK ON 2026-09-11, and the rule it used to be the example
// of is unchanged: an admin reaches a WORK SURFACE by becoming the role that
// owns it. This page stopped being one of those. A cadence belongs to a BRAND,
// not to an AM, so "put on the right lens first" meant guessing which AM held
// the brand in order to reach a page that looks the same whoever opens it —
// ceremony with nothing on the other side of it. Scripts is on this nav for the
// neighbouring reason, and Throughput and the Rubric were never lenses at all.
//
// Not to be confused with editing a batch's POST DATE on the Scripts page. That
// is a Scripts-page power, an admin holds it as every AM does, and it asks
// canEditPostDates — see persona-context.tsx.
export const SCHEDULE: NavItem = { href: "/scripts/schedule", icon: CalendarDays, label: "Schedule", category: "Creator Scripts" };

// The creator's reference: what to expect, the timing, and what the score means
// in the terms they need it in. It was on no nav at all and reachable by URL,
// which under the rule at the top of this file is not a state that exists any
// more — so it goes back where its own header says it belongs, beside the
// Training Guide on the two navs that write scripts.
export const FAQ: NavItem = { href: "/scripts/faq", icon: HelpCircle, label: "FAQ / Lessons", category: "Creator Scripts" };

// Both feedback channels on one page: the disagreements filed against a grade,
// and the stars left on a page. A correction is an argument about the rubric,
// which is Script Grading's page, reached from Skills.
export const FEEDBACK: NavItem = { href: "/feedback", icon: MessageSquare, label: "Feedback", category: "Admin" };

// How much work is going through, and where it is stuck. THE THREE ROLES THAT
// RUN THE AGENCY — an AM reads their own pod, ops the pods they cover, an admin
// everything — and the scoping is in the query rather than in this list, so all
// three open the same page. It is on nobody
// else's nav, which under the rule at the top of this file is the whole gate: a
// creator, a writer or a brand user who types the URL gets a 404.
export const THROUGHPUT: NavItem = { href: "/throughput", icon: Gauge, label: "Throughput", category: "Creator Scripts" };

// THE BOOK — every brand side by side, and what each relationship is actually
// doing. EVERY ROLE THAT RUNS THE AGENCY since 2026-09-19, and the scoping is
// in the query rather than in this list — an AM's table holds their own pod,
// and the pod pills narrow it further; neither decides who may open it.
//
// ⚠️ IT HAS A HEADING OF ITS OWN, AND IT IS THE ONLY THING UNDER IT. "Brand
// Health" sat at the bottom of Creator Scripts, under a heading about the work
// going out; it is about the CLIENTS that work is for, which is a different
// question by the same reader. A one-entry section is not a mistake here — the
// rule that a lone category gets no heading is about a role whose WHOLE nav is
// one category, and this one names a third subject between the work and the
// intake. It is also where the rest of the book will land: the record beneath
// it already exists at /brands/<slug>.
//
// ⚠️ "BRAND HEALTH", NOT "BRANDS", and the label is the reason the section can
// be called Brands at all. A "Brands" entry under a "Brands" heading says the
// same word twice and neither time says what the page is; Health is what the
// table's first column actually is, and what the reader comes for.
//
// ⚠️ NOT TO BE CONFUSED WITH "Brands & access" UNDER Admin. That page is where a
// brand EXISTS at all — its label, its pod, the vendor accounts and grants that
// measure it. This one is the relationship. They stay in different sections on
// purpose: identity is the console's housekeeping, health is a reading.
export const BRANDS_BOOK: NavItem = { href: "/brands", icon: BookOpen, label: "Brand Health", category: "Brands" };

// THE WEEKLY REPORT — each brand's report for the week just closed, the page the
// AM takes into the brand meeting.
// "Weekly Reports", plural, because the page is the list of them (mock round 2).
// Under Brands, beside Brand Health, because both are readings of one brand.
//
// It is on the AM's, Ops' and the admin's navs: the AM is who it is FOR. It was
// the admin's alone while the feature was being built (a release hold in
// lib/route-access.ts, since lifted).
//
// ⚠️ IT SITS INSIDE BRAND HEALTH'S SUBTREE (/brands/weekly, and
// /brands/<slug>/weekly/… under a brand's record), which is the case isNavActive
// has to be told about, like Insights under the checklist.
export const WEEKLY_REPORTS: NavItem = { href: "/brands/weekly", icon: FileText, label: "Weekly Reports", category: "Brands" };

// AFFILIATE CAMPAIGNS — where the prototype's sidebar puts them: the work
// table and the metrics under Brands, after Brand Health and Weekly Reports (the
// two pages about every brand come first), and Reacher writes (the key pool and
// every write) under Admin. The DM Replies skill is a row on the Skills page, as
// every skill is.
//
// The work table and the metrics are on the AM's, Ops' and the admin's navs, the
// roles /brands gives them. Reacher writes stays the admin's. They were the admin's
// alone while they were being built, a hold since lifted.
export const AFFILIATE_CAMPAIGNS: NavItem = { href: "/brands/affiliates", icon: Megaphone, label: "Affiliate Campaigns", category: "Brands" };
export const AFFILIATE_METRICS: NavItem = { href: "/brands/affiliate-metrics", icon: ChartLine, label: "Affiliate Metrics", category: "Brands" };
// CLIENT SLACK: Response Times, the Client Inbox and Action Items, under Brands
// after the Weekly Reports, where the mock's sidebar puts them. Response Times
// is on AM Ops' and the admin's navs; the Client Inbox and Action Items on the
// AM's too, the roles their route-access lines give (never the Creative
// Specialist's). Inside Brand Health's subtree, so isNavActive carves them out
// of it.
export const RESPONSE_TIMES: NavItem = { href: "/brands/response-times", icon: Timer, label: "Response Times", category: "Brands" };
export const CLIENT_INBOX: NavItem = { href: "/brands/inbox", icon: Inbox, label: "Client Inbox", category: "Brands" };
export const ACTION_ITEMS: NavItem = { href: "/brands/action-items", icon: ListTodo, label: "Action Items", category: "Brands" };
// THE OPERATIONS INBOX: every task a seat owes, a card each. Under Admin on
// the admin's, AM Ops' and the AM's navs, first in that heading, with a badge
// of what is left to do today (app/layout.tsx). ⚠️ NOT FIRST IN ANY NAV, ON
// PURPOSE: the first entry is where "/" lands a role (homeRouteFor), and
// nobody's landing changes while the inbox is rolled out a pod at a time.
export const OPS_INBOX: NavItem = { href: "/inbox", icon: ClipboardCheck, label: "Operations Inbox", category: "Admin" };
export const REACHER_WRITES: NavItem = { href: "/settings/integrations/reacher-writes", icon: KeyRound, label: "Reacher writes", category: "Admin" };

// Every model call the app makes and what it cost, a tab per budget. Under
// /settings, admin's and AM Ops' by a route_access line of its own, since the
// section admits the AM too (for Your Slack).
export const LLM_COSTS: NavItem = { href: "/settings/llm-costs", icon: Coins, label: "LLM costs", category: "Admin" };

// THE SKILLS PAGE: every
// skill the app runs, Active and Retired, each row linking to its own page at
// /skills/<slug>. The ONE entry for all of them, under Admin beside LLM costs,
// whose today-against-the-limit cell its rows repeat. It took the place of
// the Skills heading's twenty items and of Retired skills, now its second tab.
// AM Ops' and the admin's, /skills' own node in lib/route-access.ts. It is lit
// on every page under /skills and on a grading run's page (isNavActive).
export const SKILLS_NAV: NavItem = { href: SKILLS_PATH, icon: Sparkles, label: "Skills", category: "Admin" };

// YOUR OWN SLACK CONNECTION: Connect Slack, and Disconnect, for replying from
// the Client Inbox as yourself. Under /settings, so it is the AM's, AM Ops' and
// the admin's with no route_access line of its own: /settings holds the AM for
// this page alone, and every other page under it that an AM must not open has
// its own line. The page asks the inbox's question itself too.
export const YOUR_SLACK: NavItem = { href: "/settings/slack", icon: AtSign, label: "Your Slack", category: "Admin" };

// WHETHER THE APP IS GETTING WORSE: request times, failures, slow statements,
// jobs and the health probe, from pilot-ops.db. Under /settings/integrations,
// so it is the admin's with no route_access line, as Reacher writes is.
export const OPS_HEALTH: NavItem = { href: "/settings/integrations/health", icon: Activity, label: "App health", category: "Admin" };

// EVERY SCHEDULED JOB, VISIBLE WHILE IT RUNS: which job is running, in which
// step, for how long against its interval, what waits behind it, and each job's
// history. The admin's alone, like App health beside it: its own route_access
// line, since /admin is AM Ops' too. It took the ops page's Jobs tab, whose
// counts are on it.
export const JOBS_PAGE: NavItem = { href: "/admin/jobs", icon: CalendarClock, label: "Jobs", category: "Admin" };

// CPU AND MEMORY OVER TIME: the box, every container against its limits, and
// the processes inside them, from pilot-ops.db. The admin's alone, as Jobs
// beside it: its own route_access line, since /admin is AM Ops' too. App
// health's icon, as the approved mock draws it: the two are the same kind of
// page, how the app itself is doing.
export const RESOURCES_PAGE: NavItem = { href: "/admin/resources", icon: Activity, label: "Resources", category: "Admin" };

// THE FEATURE FLAGS: the behaviour switches that were the
// host's .env, set with no restart. The admin's alone, its own route_access line.
export const FEATURE_FLAGS: NavItem = { href: "/settings/flags", icon: ToggleRight, label: "Feature flags", category: "Admin" };

// A NEW CLIENT'S FIRST THIRTY DAYS — the onboarding checklist, its gates and
// its call log. THE SAME THREE ROLES AS THROUGHPUT and for the same reason: it
// is the agency's own work surface, an AM reads their pod's clients, ops the
// pods they cover, an admin all of them. It is on no creator, writer or brand
// nav, which under the rule at the top of this file is the whole gate — the
// checklist carries every client's call notes and the reason we have not
// invoiced them yet.
//
// It is a CLIENT of nav-config in a second way as well: /api/onboarding/first30/* asks
// canOpen on this same href through lib/server/api-gate.ts, so the page and its
// fifteen routes cannot disagree about who is allowed in — and since those
// routes moved under /api/onboarding, the URL says it too.
//
// ⚠️ "CLIENT ONBOARDING", NOT "ONBOARDING". A console nav has three entries
// with "onboarding" in them and they are about three different things — this
// one is a CLIENT's thirty days, Insights is how long its steps sit waiting,
// and the Template is the standard they are all seeded from. Naming the noun
// is what tells them apart at a glance, and it is the same phrase the page
// title and the checklist's crumb now use.
export const ONBOARDING_FIRST30: NavItem = {
  href: "/onboarding/first30",
  icon: Rocket,
  label: "Client Onboarding",
  category: "Onboarding",
};

// HOW LONG EACH ONBOARDING STEP SITS WAITING — the fold over the readiness
// clock, on THE SAME THREE ROLES as the checklist it measures. It is not a
// narrower permission and it is not trying to be: an AM who can read their
// pod's checklists can read how long their pod's steps took, and the fold is
// scoped by the same lens the checklists are (lib/server/first30-spans.ts).
//
// ⚠️ IT IS A SECOND ENTRY OVER ONE SUBTREE, which is the case isNavActive has
// to be told about. `/onboarding/first30` owns its descendants, so without the
// rule below BOTH links light up here and the sidebar says you are in two
// places. Settings and Users are the same shape and the same fix. Being a
// second entry buys nothing permission-wise — the layout gate above it still
// asks the CHECKLIST's list, because gateFor is given a fixed path — and that
// is deliberate: two lists over one gate would be two answers to one question.
export const ONBOARDING_INSIGHTS: NavItem = {
  href: "/onboarding/first30/insights",
  icon: BarChart3,
  label: "Onboarding Insights",
  category: "Onboarding",
};

// THE ONBOARDING TEMPLATE ITSELF — the checklist every new client is seeded
// from, as a page rather than as a constant somebody has to ask us to change.
// ADMIN AND AM OPS, which is a NARROWER list than the checklist above it: an AM
// works a client's own copy, and the template is the agency's standard rather
// than one pod's. AM Ops holds it.
//
// ⚠️ IT LIVES UNDER /settings AND CANNOT LIVE UNDER /onboarding, and the reason
// is isNavActive rather than taste. A subtree match means the ONBOARDING_FIRST30
// entry would own /onboarding/first30/anything — so an `am`, who has that
// entry, would be admitted to a template editor placed beneath it, gate and
// all. Under /settings it is a page of its own with a list of its own, which is
// the same reason USERS sits where it does.
export const ONBOARDING_TEMPLATE: NavItem = {
  href: "/settings/onboarding-template",
  icon: ListChecks,
  label: "Onboarding Template",
  category: "Onboarding",
};

// CREATOR SELECTION — paste a casting brief, get a ranked shortlist off the
// roster with the evidence, and record what was decided about each candidate.
// THE SAME THREE ROLES AS THROUGHPUT AND ONBOARDING, and for the same reason:
// it is the agency's own work surface. The roster is agency-wide and
// deliberately unscoped — an AM searching the whole of it is the intent, not a
// leak — so the scoping this list does is the only scoping there is.
//
// It is a CLIENT of nav-config twice over, like the checklist: /api/sourcing/*
// asks canOpen on this same href through lib/server/api-gate.ts, so the page
// and its ten routes cannot disagree about who is allowed in. Those routes
// shipped ahead of this line and answered 403 for EVERY role until it landed,
// which is what "nothing user-visible ships until it works" costs and buys.
// ⚠️ "CREATOR SELECTION", NOT "SOURCING" AND NOT "CREATOR SOURCING". Two
// decisions, one line:
//
//   IT NAMES ITS NOUN, because the heading above it does not. Under
//   "Onboarding" a bare "Sourcing" does not say what is being sourced, and the
//   entry beside it is "Client Onboarding" — the intake's two halves, which
//   read as a pair only when both say whose.
//
//   AND IT IS THE PAGE'S OWN NAME. The href stays /sourcing and the feature is
//   still `sourcing` throughout the code, but "Creator selection" is what the
//   page has called itself since it shipped. A nav entry and the title it lands
//   on saying two different things is the drift the Client Onboarding rename
//   spent three surfaces removing.
export const SOURCING: NavItem = { href: "/sourcing", icon: UserSearch, label: "Creator Selection", category: "Onboarding" };

// HOW SELECTION IS GOING — briefs run, creators pitched, how long a pitch takes
// to become an approval, and which gate is doing the excluding. THE SAME THREE
// ROLES AS THE PAGE IT MEASURES, for the reason Onboarding Insights gives about
// the checklist: whoever may read the work may read how the work is going.
//
// ⚠️ IT IS A SECOND ENTRY OVER ONE SUBTREE, exactly as Onboarding Insights is,
// and isNavActive is told about it below. `/sourcing` owns its descendants —
// which is what keeps /sourcing/[id], the one casting a person drills into,
// lit under Creator Selection — so without that rule BOTH links light up here
// and the sidebar says you are in two places.
//
// THE ICON IS ONBOARDING INSIGHTS' OWN, ON PURPOSE. The two entries sit next to
// each other with the same mark because they are one kind of page — a dashboard
// over a flow — and the labels are what tell them apart. Giving the second one a
// different chart glyph would say they are different kinds of thing.
//
// ⚠️ AND BEING A SECOND ENTRY BUYS NOTHING PERMISSION-WISE. app/sourcing/layout
// .tsx gates the whole subtree on "/sourcing" because gateFor is given a fixed
// path, which is deliberate: two lists over one gate would be two answers to
// one question. This entry decides where the LINK is, not who gets in.
export const SOURCING_INSIGHTS: NavItem = {
  href: "/sourcing/insights",
  icon: BarChart3,
  label: "Selection Insights",
  category: "Onboarding",
};

// HOW MANY HOURS OF WORK WERE DELIVERED, a week at a time — three numbers
// counted off the tools and one typed in. AM OPS AND THE ADMIN, which is the
// brand book's list rather than Throughput's (lib/route-access.ts's /client-hours
// line carries the argument), so it is the third entry on the console and on no
// AM's nav.
//
// ⚠️ A CLOCK, NOT A CHART GLYPH, and that is the one thing separating it from
// the two Insights entries above it. Those two share BarChart3 because they are
// one kind of page — a dashboard over a flow — and this is not: it is a count of
// TIME, and the unit is the whole point of the page. The label says whose hours
// and the icon says what kind of number.
export const CLIENT_HOURS: NavItem = {
  href: "/client-hours",
  icon: Clock,
  label: "Client Hours",
  category: "Reports",
};

// THREE CATEGORIES since Brand Health opened to AMs (2026-09-19), under the
// same heading and in the same place as the console's: the work, then the
// brands it is for, then the intake.
export const FATHOM: NavItem = { href: "/meetings/fathom", icon: CalendarDays, label: "Your Fathom", category: "Admin" };

// THE AM'S WORK NAV, AND THE CREATIVE SPECIALIST'S. They were one shared list
// until the Operations Inbox opened: the AM's gained the inbox at the top, the
// Client Inbox and Action Items under Brands, and Your Slack under Admin, and
// the CS none of them (their route-access lines name the AM alone). So the CS's
// list is the AM's as it was, written out, and an entry for both is added to
// both.
const CS_NAV: NavItem[] = [
  SCRIPTS, SCHEDULE, THROUGHPUT,
  BRANDS_BOOK, WEEKLY_REPORTS, AFFILIATE_CAMPAIGNS, AFFILIATE_METRICS,
  ONBOARDING_FIRST30, SOURCING, ONBOARDING_INSIGHTS, SOURCING_INSIGHTS,
];


/**
 * ROLE → THE PAGES IT HAS. First entry is where "/" sends that role, so order
 * is meaningful.
 */
// ⚠️ AND SINCE 2026-09-07 ORDER IS MEANINGFUL TWICE OVER: the sidebar cuts each
// list into headings wherever the category changes (navSectionsFor, below), so
// these arrays are grouped by category and ordered within it. Interleaving two
// categories does not regroup them — it renders the same heading twice. Keep a
// category contiguous, and keep the categories in NAV_CATEGORIES' order.
//
// SCHEDULE stays inside Creator Scripts and never leads it: the first entry IS
// the role's landing route, and the config page is not where an AM should start
// their day.
//
// Admin is a CONSOLE, not a superset: what belongs to running the app — the
// rubric the grader scores against, the feedback about it, the roster, the Drive
// connection. The rule that keeps it from becoming a superset is unchanged: an
// admin reaches a WORK SURFACE by BECOMING the role that owns it, which is what
// the persona dropdown is for. (Brand Guide and Reports were absent from every
// list here for a month before anyone noticed the pages were still live and
// ungated; they are deleted now.)
//
// ⚠️ SCHEDULE USED TO BE THIS RULE'S EXAMPLE AND IS NOW ON THE CONSOLE. The rule
// is about pages where the LENS CHANGES WHAT YOU SEE — a roster, a pod, one AM's
// week. A brand's cadence is none of those: it belongs to the brand, it looks
// the same to whoever opens it, and the only thing "become the AM first" bought
// was guessing which AM held the brand. The entry's own comment carries the
// argument.
//
// ⚠️ ABSENT NO LONGER MEANS REFUSED, and that is the one thing to re-read if
// you knew this file before. Leaving a route off a role's list now leaves them
// without a LINK to it; whether they may open it is lib/route-access.ts. The two
// are held in step by a test rather than by being the same array — see "nav is a
// subset of the tree" in tests/unit/route-access.test.ts, which fails on a link
// no one can follow and on a page no one can click to.
//
// SCRIPTS IS THE EXCEPTION, and since 2026-09-02 for a simpler reason than it
// used to be: on that page an admin IS an AM — same view, same buttons, same
// delete chip — so there is no lens to put on first, and no reason to keep the
// work off the console's nav. (It used to carry two admin-only tools, the chip
// that moved a week between brands and the no-brand bucket it emptied; both
// were repairs for data that is now clean, and both are gone.)
//
// It goes FIRST, so it is also where "/" drops an admin (homeRouteFor). That is
// a deliberate second change and not a side effect: an admin arriving at the app
// is far more often coming to look at the work than to re-read the rubric.
//
// Every working role also leads with Scripts: the AM and AM Ops carry the
// Schedule beside it (and the Operations Inbox under Admin, never first), the
// creator and their writer carry the two reference pages, and a brand carries
// nothing at all — the work is the whole of the app for them.
//
// ⚠️ FEEDBACK AND GRADED RUNS DO NOT SIT TOGETHER ANY MORE, and that is the one
// thing the 2026-09-07 grouping broke on purpose. They used to be adjacent
// because "a correction argues with a rubric about a snapshot" is one sitting —
// but Graded Runs and the Rubric are about the SCRIPTS, and Feedback is the
// channel the app itself is complained through, which is the console's. The
// rubric and its runs are now Script Grading's, reached from Skills.
//
// ⚠️ NO SKILL HAS AN ENTRY OF ITS OWN: Skills, under
// Admin, is the one, and its page lists which are live and which retired, so
// retiring or reviving a skill is still one field in skills/skills.ts and
// touches no nav.
export const NAV_BY_ROLE: Record<Persona["role"], NavItem[]> = {
  // ONE CATEGORY, SO NO HEADINGS — see NAV_CATEGORIES. All three of these are
  // Creator Scripts, and a lone "CREATOR SCRIPTS" over the whole sidebar would
  // indent a creator's three links to tell them nothing.
  creator: [SCRIPTS, TRAINING_GUIDE, FAQ],
  // THE CREATOR'S NAV, ENTRY FOR ENTRY. A writer does the creator's writing, so
  // they need the surface it happens on and the guide that says how — and
  // nothing else, because everything else on any other nav is a thing they are
  // not allowed to do. What differs between the two lives in script-access.ts:
  // this table decides which PAGES you reach, and a writer reaches a creator's;
  // that file decides what the two may do once they are both on one.
  writer: [SCRIPTS, TRAINING_GUIDE, FAQ],
  brand: [SCRIPTS],
  // TWO PAGES, AND NOTHING ELSE IN THE APP. A Creator Expert casts: they run
  // briefs against the roster and read how the casting is going. They are not
  // an AM with a narrower nav — they hold no pod, write no scripts, approve
  // nothing, and never see one. Under the rule at the top of this file that
  // absence IS the gate: /scripts, /throughput, /onboarding/first30 and the
  // rest answer 404 for them, and /api/sourcing/* admits them for exactly the
  // same reason it admits an AM, because it asks canOpen on "/sourcing".
  //
  // ONE CATEGORY, SO NO HEADINGS — the same rule the creator's three links
  // answer to. Both entries are Onboarding, and a lone "ONBOARDING" over the
  // whole sidebar would indent two links to tell nobody anything.
  //
  // ⚠️ NOT canReconcileBrandLinks, WHICH IS BELOW AND STAYS AS IT IS. The FCC
  // brand-link banner is the console's housekeeping over which client a
  // spreadsheet key belongs to — admin and AM Ops — and a Creator Expert is
  // further from that than the AM who already does not get it.
  creator_expert: [SOURCING, SOURCING_INSIGHTS],
  // The AM's work, then under Admin the Operations Inbox and their own
  // connections: Your Slack (replying from the Client Inbox, reminding a
  // creator) and Your Fathom, the AM's and the admin's.
  am: [
    SCRIPTS, SCHEDULE, THROUGHPUT,
    BRANDS_BOOK, WEEKLY_REPORTS, CLIENT_INBOX, ACTION_ITEMS, AFFILIATE_CAMPAIGNS, AFFILIATE_METRICS,
    ONBOARDING_FIRST30, SOURCING, ONBOARDING_INSIGHTS, SOURCING_INSIGHTS,
    OPS_INBOX, YOUR_SLACK, FATHOM,
  ],
  // A Creative Specialist starts on an AM's powers, but not the AM's inboxes:
  // see CS_NAV.
  creative_specialist: CS_NAV,
  // AN AM'S NAV PLUS THE CONSOLE'S READING ROOM. Ops works the pods they cover
  // with an AM's powers, and also reads what running the app produces — the
  // skills, the feedback about them, the roster, and since
  // 2026-09-11 the brand book, whose missing-knobs banner was addressed to this
  // role by name while only an admin could see it. NOT Settings:
  // the Drive grant is the one thing on that page, and it is the admin's, which
  // is why ops' Admin heading lacks Settings (and Reacher writes beneath it).
  // LLM costs is both consoles': ops runs the features it charts, and Skills is
  // both consoles' for the same reason.
  am_ops: [
    SCRIPTS, SCHEDULE, THROUGHPUT,
    BRANDS_BOOK, WEEKLY_REPORTS, RESPONSE_TIMES, CLIENT_INBOX, ACTION_ITEMS, AFFILIATE_CAMPAIGNS, AFFILIATE_METRICS,
    ONBOARDING_FIRST30, SOURCING, ONBOARDING_INSIGHTS, SOURCING_INSIGHTS, ONBOARDING_TEMPLATE,
    CLIENT_HOURS,
    OPS_INBOX, USERS, BRANDS_ADMIN, LLM_COSTS, SKILLS_NAV, YOUR_SLACK, FEEDBACK,
  ],
  // THE SAME SCRIPTS ENTRIES OPS HAS, IN THE SAME ORDER, and the same book —
  // the two consoles differ by Settings and by nothing else above
  // the Admin section now.
  admin: [
    SCRIPTS, SCHEDULE, THROUGHPUT,
    BRANDS_BOOK, WEEKLY_REPORTS, RESPONSE_TIMES, CLIENT_INBOX, ACTION_ITEMS, AFFILIATE_CAMPAIGNS, AFFILIATE_METRICS,
    ONBOARDING_FIRST30, SOURCING, ONBOARDING_INSIGHTS, SOURCING_INSIGHTS, ONBOARDING_TEMPLATE,
    CLIENT_HOURS,
    OPS_INBOX, USERS, BRANDS_ADMIN, SETTINGS, REACHER_WRITES, OPS_HEALTH, JOBS_PAGE, RESOURCES_PAGE, FEATURE_FLAGS, LLM_COSTS, SKILLS_NAV, YOUR_SLACK, FATHOM, FEEDBACK,
  ],
};

/**
 * A ROLE'S NAV, CUT INTO THE HEADINGS IT RENDERS UNDER.
 *
 * ⚠️ THE ORDER IS THE ARRAY'S, NOT NAV_CATEGORIES'. This walks the role's list
 * once and starts a new section wherever the category changes, so the array
 * above is the single authority on both what a role has AND what order it is
 * in — there is no second list of headings to reorder in step with it, and
 * NAV_CATEGORIES only names the strings and says which are legal.
 *
 * The consequence is worth stating: a list that interleaves categories
 * (Scripts, Users, Throughput) produces an "Admin" heading in the middle and
 * then a second "Creator Scripts" one, rather than silently regrouping. That is
 * the honest rendering of a list somebody has mis-ordered, and it looks wrong
 * on the screen where the mistake can be seen, instead of looking right on a
 * screen that disagrees with the file.
 *
 * ⚠️ AND IT IS NOT A PERMISSION. Grouping is presentation, and so now is the
 * table it groups — no gate anywhere depends on this function being called or
 * being right.
 */
export interface NavSection {
  category: NavCategory;
  items: NavItem[];
}

export function navSectionsFor(role: Persona["role"]): NavSection[] {
  const sections: NavSection[] = [];
  for (const item of NAV_BY_ROLE[role]) {
    const open = sections[sections.length - 1];
    if (open && open.category === item.category) open.items.push(item);
    else sections.push({ category: item.category, items: [item] });
  }
  return sections;
}

/**
 * The Account Record's subtree — one client's record and the deck beneath it.
 *
 * ⚠️ NOT A NavItem, AND IT MUST NOT BECOME ONE. There is no sidebar link to a
 * record: you reach one by clicking a client on /onboarding/first30, exactly as
 * you reach that client's checklist. It is a bare path because isNavActive is
 * the only thing that needs it — and it needs it because the entry whose href
 * is /onboarding/first30 has to light on a sibling branch of /onboarding, which
 * no other carve-out in this file does.
 */
const ONBOARDING_RECORD = "/onboarding/record";

/**
 * A grading run's page, /admin/grades/<script>/<run>: what is left of the
 * grading runs' subtree now that the list is Script Grading's Graded Runs tab
 * (/admin/grades itself redirects there). A prefix with its slash for
 * isNavActive: no nav entry links to a run, and Skills owns it.
 */
const GRADING_RUN_PAGES = "/admin/grades/";

/**
 * Does this nav entry own the page you are on? WHICH LINK IS LIT, and nothing
 * else — this stopped deciding whether the page opens when the permission moved
 * to lib/route-access.ts.
 *
 * ⚠️ THAT IS WHY THE CARVE-OUTS BELOW ARE NOW HARMLESS. Each exists
 * because a sibling nav entry sits inside another entry's subtree, and without
 * it both links light up and the sidebar says you are in two places. While this
 * function was also the gate, getting one wrong was a permission bug; now the
 * worst it can do is underline the wrong word.
 *
 * The editor has no nav entry of its own: it is what a video on /scripts/videos
 * opens into, so Scripts owns it and stays lit while you are in there. A creator
 * mid-edit who is switched to is therefore NOT thrown out of the editor, which
 * is the whole reason this special case has to be shared rather than reimplemented.
 *
 * ⚠️ A SUBTREE MATCH, NOT A STRING PREFIX. `/onboarding/first30` has to own the
 * client checklists beneath it, so an entry does match its descendants — but on a SEGMENT
 * boundary, which a bare startsWith does not. That was tolerable while this only
 * decided which link to underline; it is not tolerable now that the same answer
 * opens the page, because the next route named as an extension of an existing
 * one ("/feedback-export", "/settings/users-import") would inherit its
 * permissions from a name collision. Settings, which is a prefix of Users, is
 * still pinned to an exact match on top of that — it is a different page with a
 * different list, not an ancestor of one.
 */
export function isNavActive(href: string, path: string): boolean {
  const owns = (h: string) => path === h || path.startsWith(`${h}/`);
  if (href === SCRIPTS.href) return owns(href) || owns("/scripts/editor");
  // "/settings" IS a prefix of "/settings/users", so a subtree match lights
  // both — the collision this function's docstring warned about, now real.
  // Settings owns exactly its own page; Users owns the page under it.
  if (href === SETTINGS.href) return path === href;
  // The same collision one level down: Insights is a descendant ROUTE of the
  // checklist but a sibling ENTRY, so the checklist owns everything under it
  // except that one branch. Onboarding stays lit on a client's own checklist
  // (/onboarding/first30/c/<slug>), which has no entry of its own.
  //
  // ⚠️ AND ON A CLIENT'S ACCOUNT RECORD, which is NOT under the checklist's
  // href at all — /onboarding/record/<slug> and the deck beneath it. It is the
  // same client's work reached from the same list, and without this the sidebar
  // goes dark on both pages: no entry owns /onboarding, only /onboarding/first30.
  if (href === ONBOARDING_FIRST30.href) {
    return (owns(href) || owns(ONBOARDING_RECORD)) && !owns(ONBOARDING_INSIGHTS.href);
  }
  // The same shape a third time, one feature over. Creator Selection owns
  // everything under /sourcing — including /sourcing/<casting>, which a person
  // drills into and which has no entry of its own — except the one branch that
  // IS an entry. The permission is unchanged either way: every page under it
  // answers to the "/sourcing" node, through the layout and each page's own
  // gatePage call. Its skills' pages are under /skills, which no area's entry owns.
  if (href === SOURCING.href) return owns(href) && !owns(SOURCING_INSIGHTS.href);
  // A fourth time, and the one with a slug in the middle: Brand Health owns
  // /brands and each brand's record, except the Weekly Report's pages — the list
  // at /brands/weekly and a brand's reports at /brands/<slug>/weekly/…, which
  // light Weekly Reports instead.
  //
  // ⚠️ AND AFFILIATE CAMPAIGNS, which are entries of their own under /brands:
  // the work table owns each brand's affiliate page beneath it
  // (/brands/affiliates/<slug>, and its New campaign). The metrics are a SIBLING path
  // (/brands/affiliate-metrics), which `owns` already keeps apart from the table
  // by its slash — but not from Brand Health, which owns all of /brands.
  const affiliates = owns(AFFILIATE_CAMPAIGNS.href) || owns(AFFILIATE_METRICS.href);
  // ⚠️ `/brands/affiliates/weekly` is a BRAND'S affiliate page (a brand slugged
  // `weekly` cannot exist, but the pattern below would still claim the path).
  const weekly =
    owns(WEEKLY_REPORTS.href) || (!affiliates && /^\/brands\/[^/]+\/weekly(\/|$)/.test(path));
  // ⚠️ AND CLIENT SLACK'S PAGES, entries of their own under /brands.
  const clientSlack = owns(RESPONSE_TIMES.href) || owns(CLIENT_INBOX.href) || owns(ACTION_ITEMS.href);
  if (href === BRANDS_BOOK.href) return owns(href) && !weekly && !affiliates && !clientSlack;
  if (href === WEEKLY_REPORTS.href) return weekly;
  // ⚠️ AND A GRADING RUN'S PAGE: Graded Runs left the
  // nav with every skill item, and a run is Script Grading's, so Skills owns
  // the runs under /admin/grades as it owns every /skills page (the list
  // itself is a tab of a /skills page).
  if (href === SKILLS_NAV.href) return owns(href) || path.startsWith(GRADING_RUN_PAGES);
  return owns(href);
}

/**
 * ⚠️ canOpen HAS MOVED to lib/route-access.ts, and is not re-exported here.
 *
 * It used to read NAV_BY_ROLE below, which made this file the permission as
 * well as the sidebar. Leaving a forwarding export would keep two import paths
 * for one decision, and the whole point of the split is that there is one place
 * to look. Import it from @/lib/route-access.
 */

/**
 * MAY THIS ROLE RECONCILE THE FCC BRAND LINKS — the /sourcing banner, and the
 * route behind it.
 *
 * ⚠️ THIS IS THE ONE PLACE A ROLE ANSWER IS NARROWER THAN A PAGE, and it is
 * written here, beside the rule it bends, rather than hidden in the component.
 * The rule at the top of this file is that a page IS a permission: if it is on
 * your nav you may do everything on it. That rule is about PAGES, and it holds —
 * /sourcing is one permission and all three roles that hold it get the whole
 * workbench.
 *
 * What this gates is not part of the workbench. It is a maintenance control over
 * WHICH CLIENT a spreadsheet key belongs to, and that belongs to AM Ops and
 * admin: an AM sources creators against a brief, and the mapping between the
 * recruitment sheet's 220 brands and our own is the console's housekeeping, not
 * theirs. An `am` has /sourcing and does not see the banner.
 *
 * ⚠️ AND IT IS ASKED IN BOTH PLACES, never only in the component. The route
 * (app/api/sourcing/brand-links/route.ts) asks it after apiGate, so an AM who
 * types the URL gets the same 403 the missing banner implies — a control hidden
 * in the markup and open on the wire is the shape of gate this app has already
 * had to fix once.
 */
export function canReconcileBrandLinks(role: Persona["role"] | null | undefined): boolean {
  return role === "admin" || role === "am_ops";
}
